import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import {
    MatTable,
    MatTableModule,
    MatTableDataSource,
} from '@angular/material/table';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
    ValidatorFn,
    AbstractControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router, RouterLink } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { Role } from '../../interfaces/Role';
import { RoleResponse } from '../../interfaces/RoleResponse';
import { Company } from '../../interfaces/Company';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { BranchOffice } from './../../interfaces/BranchOffice';
import { BranchOfficeResponse } from './../../interfaces/BranchOfficeResponse';
import { CompanyUserContactInfo } from '../../interfaces/CompanyUserContactInfo';
import { ContactMediumType } from '../../interfaces/ContactMediumType';
import { ContactMediumTypeResponse } from '../../interfaces/ContactMediumTypeResponse';
import { CompanyUser } from '../../interfaces/CompanyUser';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';

@Component({
    selector: 'app-m-create-company-user',
    standalone: true,
    imports: [
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSelectModule,
        ReactiveFormsModule,
        TranslocoModule,
        NgFor,
        NgIf,
    ],
    templateUrl: './m-create-company-user.component.html',
    styleUrl: './m-create-company-user.component.scss',
})
export class MCreateCompanyUserComponent implements OnInit {
    // isToggled
    isToggled = false;

    // Forms
    addCompanyUserForm: FormGroup;

    // Password Hide
    hide = true;
    hide2 = true;
    passwordsMatching = false;
    isConfirmPasswordDirty = false;

    // Array of roles
    roles: Role[] = [];

    selectedRole: Role | undefined;

    // Array of companies
    companies: Company[] = [];

    selectedCompany: string = '';

    // Array of branch offices
    branchOffices: BranchOffice[] = [];

    selectedBranchOffice: BranchOffice | undefined;

    // Array of contact medium types
    contactMediumTypes: ContactMediumType[] = [];

    selectedContactMediumType: ContactMediumType | undefined;

    contactInfos: CompanyUserContactInfo[] = [];

    // Show alert
    showAlert: boolean = false;

    // Error message
    errorMsg: string;

    readonly dialog = inject(MatDialog);

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private router: Router
    ) {
        this.addCompanyUserForm = this.fb.group(
            {
                name: ['', [Validators.required, Validators.maxLength(300)]],
                email: [
                    '',
                    [
                        Validators.required,
                        Validators.maxLength(300),
                        Validators.email,
                    ],
                ],
                password: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(8),
                        Validators.maxLength(50),
                        Validators.pattern(
                            /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!-\/:-@[-`{-~]).{8,50}/
                        ),
                    ],
                ],
                confirmPassword: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(8),
                        Validators.maxLength(50),
                        Validators.pattern(
                            /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!-\/:-@[-`{-~]).{8,50}/
                        ),
                    ],
                ],
                role: [this.selectedRole, [Validators.required]],
                branchOffice: [
                    this.selectedBranchOffice,
                    [Validators.required],
                ],
                contact: ['', [Validators.maxLength(15)]],
            },
            {
                validator: this.matchValidator('password', 'confirmPassword'),
            }
        );
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnInit() {
        this.apiService.getRole().subscribe((response: RoleResponse) => {
            this.roles.push(...response.roles);
        });
        this.apiService
            .getCompanyPagination(0, 1000000, 'name', 'asc')
            .subscribe((response: CompanyResponse) => {
                this.companies.push(...response.companies);
            });
        this.apiService
            .getContactMediumType()
            .subscribe((response: ContactMediumTypeResponse) => {
                this.contactMediumTypes.push(...response.contactMediumTypes);
            });
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Change company
    changeCompany(companyId: string) {
        this.branchOffices = [];
        this.apiService
            .getBranchOfficeByCompanyId(companyId, 0, 1000000, 'name', 'asc')
            .subscribe((response: BranchOfficeResponse) => {
                if (response.branchOffices.length > 0) {
                    this.branchOffices.push(...response.branchOffices);
                }
            });
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.addCompanyUserForm.invalid || this.classApplied) {
            this.showAlert = false;
            return;
        }

        // Disable the form
        this.addCompanyUserForm.disable();

        // Hide the alert
        this.showAlert = false;

        if(this.selectedContactMediumType != undefined) {
            const newCompanyContactInfo: CompanyUserContactInfo = {
                id: '',
                contact: this.addCompanyUserForm.get('contact')?.value,
                contactMediumId: '',
                contactMediumType: this.selectedContactMediumType,
            };
            this.contactInfos.push(newCompanyContactInfo);
        }

        // Add Company
        let newCompanyUser: CompanyUser = {
            ...this.addCompanyUserForm.value,
            contactInfos: this.contactInfos,
        };
        this.apiService.addCompanyUser(newCompanyUser).subscribe({
            next: (value) => {
                console.info(value);
                // Re-enable the form
                this.addCompanyUserForm.enable();

                // Show the alert
                this.showAlert = true;
            },
            error: (error) => {
                let errMsg: string = error;
                const dialogRef = this.dialog.open(AlertDialogComponent, {
                    data: {
                        title: 'ERROR',
                        message: error
                    },
                });
                // Re-enable the form
                this.addCompanyUserForm.enable();

                // Show the alert
                this.showAlert = true;
            },
            complete: () => {
                this.router.navigateByUrl('/masters/company-users');
            },
        });
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    // Match Validator
    matchValidator(
        controlName: string,
        matchingControlName: string
    ): ValidatorFn {
        return (abstractControl: AbstractControl) => {
            const control = abstractControl.get(controlName);
            const matchingControl = abstractControl.get(matchingControlName);

            if (
                matchingControl!.errors &&
                !matchingControl!.errors?.['confirmedValidator']
            ) {
                return null;
            }

            if (control!.value !== matchingControl!.value) {
                const error = { confirmedValidator: 'Passwords do not match.' };
                matchingControl!.setErrors(error);
                return error;
            } else {
                matchingControl!.setErrors(null);
                return null;
            }
        };
    }

}
